import React from "react";
import { motion } from "framer-motion";
import { HiOutlineMusicalNote } from "react-icons/hi2";
import { useTranslation } from "react-i18next";

interface LoadingPageProps {
  message?: string;
  icon?: React.ReactNode;
}

/** Full-page loading state with icon and animations */
export const LoadingPage: React.FC<LoadingPageProps> = ({
  message,
  icon,
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="flex flex-col items-center gap-6 bg-white dark:bg-gray-800 p-10 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="text-[#5932EA]"
        >
          {icon || (
            <HiOutlineMusicalNote className="w-14 h-14" strokeWidth={1.5} />
          )}
        </motion.div>
        <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">
          {message || t("loading")}
        </p>
      </motion.div>
    </motion.div>
  );
};

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  message?: string;
  className?: string;
}

/** Inline loading spinner with icon */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "md",
  message,
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-6 h-6 border-2",
    md: "w-10 h-10 border-2",
    lg: "w-14 h-14 border-[3px]",
  };

  return (
    <motion.div
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className={`${sizeClasses[size]} rounded-full border-gray-200 dark:border-gray-600 border-t-[#5932EA]`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      {message && (
        <p className="text-gray-600 dark:text-gray-400 text-sm animate-pulse">{message}</p>
      )}
    </motion.div>
  );
};

/** Inline music note animation for loading states */
export const LoadingMusicIcon: React.FC<{ className?: string; size?: "sm" | "md" | "lg" }> = ({
  className = "",
  size = "md",
}) => {
  const sizeClasses = { sm: "w-8 h-8", md: "w-10 h-10", lg: "w-14 h-14" };
  return (
    <motion.div
      animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className={`text-[#5932EA] ${className}`}
    >
      <HiOutlineMusicalNote className={sizeClasses[size]} strokeWidth={1.5} />
    </motion.div>
  );
};

/** Small spinner for buttons */
export const ButtonSpinner: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <motion.span
    className={`inline-block w-5 h-5 rounded-full border-2 border-white/40 border-t-white ${className}`}
    animate={{ rotate: 360 }}
    transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
  />
);

interface LoadingSkeletonCardProps {
  count?: number;
  className?: string;
}

/** Animated skeleton cards for list loading */
export const LoadingSkeletonCards: React.FC<LoadingSkeletonCardProps> = ({
  count = 3,
  className = "",
}) => (
  <div className={`space-y-4 ${className}`}>
    {[...Array(count)].map((_, i) => (
      <motion.div
        key={i}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 border-2 border-gray-100 dark:border-gray-700 mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: i * 0.08 }}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="space-y-2">
          <div className="h-4 w-full bg-gray-100 dark:bg-gray-700/70 rounded animate-pulse" />
          <div className="h-4 w-11/12 bg-gray-100 dark:bg-gray-700/70 rounded animate-pulse" />
          <div className="h-4 w-3/4 bg-gray-100 dark:bg-gray-700/70 rounded animate-pulse" />
        </div>
      </motion.div>
    ))}
  </div>
);
